import React from 'react';
import './RadialMetric.css';

/**
 * Radial Score Indicator Component
 * 
 * Displays metrics as circular progress rings with numeric values
 * - Outer ring: progress indicator (0-100%)
 * - Inner text: numeric value
 * - Label below: metric name
 * 
 * Design specs:
 * - Stroke width: 6-8px
 * - Rounded caps
 * - Same diameter for all metrics
 * - Centered numbers
 */

const RadialMetric = ({ 
    value, 
    label, 
    type = 'default', // 'avg-fit', 'top', 'eligible', 'blocked'
    maxValue = 100,
    totalCandidates = null, // For eligible/blocked normalization
    size = 60 // Circle diameter - smaller for better card alignment
}) => {
    // Calculate percentage based on metric type
    let percentage = 0;
    let displayValue = value !== null && value !== undefined ? value : 0;
    
    if (type === 'eligible') {
        if (totalCandidates && totalCandidates > 0) {
            // Normalize eligible count against total candidates
            percentage = Math.min(100, Math.round((displayValue / totalCandidates) * 100));
        } else {
            // No candidates: show 0%
            percentage = 0;
        }
    } else if (type === 'blocked') {
        if (totalCandidates && totalCandidates > 0) {
            // Inverted logic: ring fills based on lack of blockage
            const blockedPercentage = Math.min(100, Math.round((displayValue / totalCandidates) * 100));
            percentage = 100 - blockedPercentage; // Inverted
        } else {
            // No candidates: show full ring (no blockage)
            percentage = 100;
        }
    } else {
        // For avg-fit and top: direct percentage (0-100)
        percentage = Math.min(100, Math.max(0, Math.round(displayValue || 0)));
    }
    
    // Calculate stroke-dasharray for progress ring
    const strokeWidth = 5; // Thinner stroke for smaller circles
    const radius = (size - strokeWidth) / 2; // Account for stroke width
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;
    
    // Color logic per metric type
    const getColors = () => {
        switch (type) {
            case 'avg-fit':
                return {
                    stroke: 'url(#gradient-avg-fit)',
                    text: '#fff'
                };
            case 'top':
                return {
                    stroke: 'url(#gradient-top)',
                    text: '#fff'
                };
            case 'eligible':
                return {
                    stroke: 'url(#gradient-eligible)',
                    text: '#fff'
                };
            case 'blocked':
                return {
                    stroke: 'rgba(255, 255, 255, 0.2)', // Even more muted, paler
                    text: 'rgba(255, 255, 255, 0.6)'
                };
            default:
                return {
                    stroke: 'rgba(255, 255, 255, 0.4)',
                    text: '#fff'
                };
        }
    };
    
    const colors = getColors();
    
    return (
        <div className="radial-metric-container">
            <svg 
                width={size} 
                height={size} 
                className="radial-metric-svg"
                viewBox={`0 0 ${size} ${size}`}
            >
                <defs>
                    <linearGradient id="gradient-avg-fit" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="rgba(0, 166, 244, 0.5)" />
                        <stop offset="50%" stopColor="rgba(139, 74, 210, 0.5)" />
                        <stop offset="100%" stopColor="rgba(255, 0, 244, 0.5)" />
                    </linearGradient>
                    <linearGradient id="gradient-top" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="rgba(0, 166, 244, 0.4)" />
                        <stop offset="50%" stopColor="rgba(139, 74, 210, 0.4)" />
                        <stop offset="100%" stopColor="rgba(255, 0, 244, 0.4)" />
                    </linearGradient>
                    <linearGradient id="gradient-eligible" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="rgba(74, 222, 128, 0.5)" />
                        <stop offset="100%" stopColor="rgba(34, 197, 94, 0.5)" />
                    </linearGradient>
                </defs>
                
                {/* Background ring (full circle) */}
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke="rgba(255, 255, 255, 0.1)"
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                />
                
                {/* Progress ring */}
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={colors.stroke}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    transform={`rotate(-90 ${size / 2} ${size / 2})`}
                    className="radial-progress-ring"
                />
                
                {/* Center text */}
                <text
                    x={size / 2}
                    y={size / 2}
                    textAnchor="middle"
                    dominantBaseline="central"
                    className="radial-metric-value"
                    fill={colors.text}
                >
                    {displayValue !== null && displayValue !== undefined ? displayValue : '--'}
                </text>
            </svg>
            
            {/* Label below */}
            <div className="radial-metric-label">{label}</div>
        </div>
    );
};

export default RadialMetric;
